import React from 'react';
import { FiUsers, FiTrendingUp, FiClock, FiZap, FiBarChart2, FiMail, FiGlobe } from 'react-icons/fi';

const OnlineAdmission = () => {
  const features = [
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: '24/7 Online Application',
      description: 'Students can apply anytime, from anywhere. No more working hours limitations, no more physical queues. Parents and students appreciate the convenience of applying when it suits them—even at midnight.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Automated Document Verification',
      description: 'No more manual verification of documents. The system validates documents instantly—checking formats, sizes, and completeness. This reduces the administrative burden and speeds up the admission process significantly.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Real-Time Application Tracking',
      description: 'Students and parents can track the status of their applications in real-time. They know exactly where they stand—application submitted, documents verified, payment confirmed, seat allotted. No more anxious waiting.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Integrated Payment Processing',
      description: 'Online fee payment is integrated into the admission process. Students can pay application fees, registration fees, and tuition deposits online. Multiple payment options make it convenient for everyone.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiMail className="w-6 h-6" />,
      title: 'Automated Communication',
      description: 'Automatic emails and SMS notifications at every stage—application received, documents needed, payment confirmation, interview schedule, admission offer. No more manual communication effort.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Admission Analytics Dashboard',
      description: 'Real-time analytics on admission trends—application numbers, conversion rates, demographic data, program popularity. Make data-driven decisions about admission strategies.',
      color: 'bg-blue-50'
    }
  ];

  const benefits = [
    {
      title: 'Fill Seats Faster',
      description: 'Automated processes mean faster decisions. Students get offers quickly, and institutions fill seats sooner.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Reach More Students',
      description: 'Online applications extend your reach beyond local areas. Students from any location can apply.',
      icon: <FiGlobe className="w-5 h-5" />
    },
    {
      title: 'Reduce Administrative Work',
      description: 'Automate document verification, payment processing, and communication. Free up staff for more important work.',
      icon: <FiClock className="w-5 h-5" />
    },
    {
      title: 'Better Candidate Experience',
      description: 'A smooth, transparent process creates a positive impression. Candidates remember their experience.',
      icon: <FiUsers className="w-5 h-5" />
    }
  ];

  const challenges = [
    {
      title: 'Manual Application Processing',
      description: 'Paper forms, manual data entry, and physical document verification slow everything down.'
    },
    {
      title: 'Document Verification Bottlenecks',
      description: 'Verifying documents manually is time-consuming and error-prone. It creates bottlenecks in the process.'
    },
    {
      title: 'Communication Delays',
      description: 'Without automated communication, candidates are left waiting. This leads to anxiety and sometimes lost applications.'
    },
    {
      title: 'Limited Candidate Reach',
      description: 'Offline admissions are limited to local candidates. You miss out on talent from other areas.'
    },
    {
      title: 'No Real-Time Visibility',
      description: 'Without analytics, you don\'t know which programs are filling, which are struggling, or where to focus your efforts.'
    },
    {
      title: 'Lost Applications',
      description: 'Paper forms get lost, misplaced, or damaged. Online systems ensure every application is tracked and secure.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        The admission season is one of the most critical periods for any educational institution. It's when you attract students, build your incoming class, and set the foundation for the academic year. But it's also one of the most stressful times for administrators, students, and parents.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">Traditional admission processes are broken.</strong> Paper forms, manual data entry, physical document verification, endless queues, and anxious waiting. Students miss deadlines. Documents get lost. Staff are overwhelmed. And institutions lose promising candidates to other colleges with faster, smoother processes.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Online admission management is transforming how Indian institutions fill seats. Here's why it matters.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">70%</div>
          <div className="text-sm text-slate-500">Faster admission processing</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">50%</div>
          <div className="text-sm text-slate-500">Reduction in admission staff workload</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">30%</div>
          <div className="text-sm text-slate-500">Increase in applicant reach</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Key Features of Online Admission Management</h2>

      {/* Features List */}
      {features.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">{item.description}</p>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">What Online Admission Delivers</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 flex items-start gap-3">
              <div className="bg-white rounded-lg p-2 flex-shrink-0">
                {item.icon}
              </div>
              <div>
                <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                <p className="text-xs text-slate-600">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Challenges Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Challenges of Traditional Admission Processes</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {challenges.map((item, i) => (
            <div key={i} className="bg-rose-50 rounded-xl p-4 border border-rose-200">
              <div className="flex items-start gap-2">
                <span className="text-rose-500 font-bold text-sm">⚠️</span>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Manual vs. Online Admission</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Manual Admission Process</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Paper forms and physical queues</span></li>
              <li className="flex items-start gap-2">• <span>Manual data entry and verification</span></li>
              <li className="flex items-start gap-2">• <span>Slow document processing</span></li>
              <li className="flex items-start gap-2">• <span>Manual communication</span></li>
              <li className="flex items-start gap-2">• <span>Limited to local applicants</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Online Admission Management</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>24/7 online applications</span></li>
              <li className="flex items-start gap-2">• <span>Automated data entry and verification</span></li>
              <li className="flex items-start gap-2">• <span>Instant document processing</span></li>
              <li className="flex items-start gap-2">• <span>Automated communication</span></li>
              <li className="flex items-start gap-2">• <span>National and international reach</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for Online Admissions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile-Friendly Applications</h4>
            <p className="text-xs text-slate-600">Apply from any device—phone, tablet, or computer</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📄</div>
            <h4 className="font-bold text-slate-900 text-sm">Auto Document Verification</h4>
            <p className="text-xs text-slate-600">Instant validation of uploaded documents</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💳</div>
            <h4 className="font-bold text-slate-900 text-sm">Integrated Payments</h4>
            <p className="text-xs text-slate-600">Secure online fee collection with multiple options</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Real-Time Analytics</h4>
            <p className="text-xs text-slate-600">Track applications, conversion rates, and trends</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💬</div>
            <h4 className="font-bold text-slate-900 text-sm">Automated Communication</h4>
            <p className="text-xs text-slate-600">Automatic emails and SMS at every stage</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔄</div>
            <h4 className="font-bold text-slate-900 text-sm">Seamless Integration</h4>
            <p className="text-xs text-slate-600">Connects with your student management system</p>
          </div>
        </div>
      </div>

      {/* The Candidate Journey */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">The Online Admission Journey</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📝</div>
            <h4 className="font-bold text-slate-900 text-sm">Step 1</h4>
            <p className="text-xs text-slate-600">Submit online application</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center">
            <div className="text-2xl mb-1">📄</div>
            <h4 className="font-bold text-slate-900 text-sm">Step 2</h4>
            <p className="text-xs text-slate-600">Upload and verify documents</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center">
            <div className="text-2xl mb-1">💳</div>
            <h4 className="font-bold text-slate-900 text-sm">Step 3</h4>
            <p className="text-xs text-slate-600">Pay fees online</p>
          </div>
          <div className="bg-rose-50 rounded-xl p-4 border border-rose-200 text-center">
            <div className="text-2xl mb-1">🎉</div>
            <h4 className="font-bold text-slate-900 text-sm">Step 4</h4>
            <p className="text-xs text-slate-600">Receive admission offer</p>
          </div>
        </div>
      </div>

      {/* The Student Perspective */}
      <div className="mt-8 bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
        <h3 className="text-lg font-bold text-emerald-900 mb-3">Why Students and Parents Prefer Online Admissions</h3>
        <ul className="text-emerald-800 leading-relaxed space-y-2">
          <li className="flex items-start gap-2">• <span>No need to visit campus multiple times</span></li>
          <li className="flex items-start gap-2">• <span>Apply from home at their convenience</span></li>
          <li className="flex items-start gap-2">• <span>Real-time status updates and transparency</span></li>
          <li className="flex items-start gap-2">• <span>Instant confirmation and communication</span></li>
          <li className="flex items-start gap-2">• <span>Secure and verified document submission</span></li>
          <li className="flex items-start gap-2">• <span>No paperwork or physical document risks</span></li>
        </ul>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Fill Seats Faster with Online Admissions</h3>
        <p className="text-indigo-800 leading-relaxed">
          The admission season is your opportunity to attract the best students. A smooth, efficient admission process creates a positive impression that lasts. Students choose institutions that make the process easy and transparent.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Online admission management transforms a stressful process into a seamless experience. For students, parents, and your staff. It fills seats faster, reaches more candidates, and builds your brand reputation.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for online admissions.</strong> 24/7 applications. Automated verification. Real-time tracking. Integrated payments. Ready to help you fill seats faster.
        </p>
      </div>
    </>
  );
};

export default OnlineAdmission;